import { EditIcon, IconButton, makeStyles, Theme } from '@freighthub/silhouette-core'
import {
  Address,
  AutocompleteDataAttributes,
  AutocompleteResult,
  ContactsAutocomplete,
  GooglePlace,
  ILocationModelJSON,
  IOrganizationJSON,
  isLocation,
  isPort,
  Location,
  LocationDialog,
  NetworkEntitySelector,
  OrganizationAutocomplete,
  PlacesSearchType,
  Port,
  TLocation,
} from 'features/Directory'
import { useObserver } from 'mobx-react-lite'
import { FC, ReactElement, useState } from 'react'
import { useRootStore } from 'service/useRootStore'
import { UnleashFeatureFlagKeys, useUnleashFeatureFlags } from 'service/useUnleashFeatureFlags'

import { TPlaceData } from '../fromMobx'
import { GenericLocationType } from '../QuoteRequest/genericLocationType'
import {
  filterResults,
  formatResults,
  getTpModeByPlaceTypes,
  placeToAutocompleteResult,
} from '../QuoteRequest/QRAutocompleteOptionUtils'

function googlePlaceToAddress(place: GooglePlace, details): Address {
  return {
    ...place,
    googplace_types: place.types,
    location: { lat: details.latitude, lng: details.longitude },
    types: ['address'],
    country: details.country,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  } as any
}

const MAX_RESULTS_FOR_TYPE = 10

const useStyles = makeStyles<Theme>((theme) => ({
  outerContainer: {
    position: 'relative',
    width: '100%',
  },
  inputAction: {
    position: 'absolute',
    right: theme.spacing(8),
    zIndex: 999,
  },
}))

type QRAutocompleteProps = {
  inputId?: string
  placeholder?: string
  value?: TPlaceData | Address
  canModifyDirectory?: boolean
  filter: string
  types: PlacesSearchType[]
  onSelect: (place: GenericLocationType) => void
  onSearchTextChange?: (query: string) => void
  dataAttributes?: AutocompleteDataAttributes
}

export const QRAutocomplete: FC<QRAutocompleteProps> = ({
  filter,
  types,
  placeholder,
  onSelect,
  canModifyDirectory,
  inputId,
  onSearchTextChange,
  dataAttributes,
  value,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formLocation, setFormLocation] = useState(Location.create({}))
  const [formOrganization, setFormOrganization] = useState<IOrganizationJSON>()
  const [selectedLocation, setSelectedLocation] = useState<ILocationModelJSON | null>()
  const { isEnabled } = useUnleashFeatureFlags()
  const isTPPortsEnabled = isEnabled(UnleashFeatureFlagKeys.TRANSPORT_PLAN_PORTS)

  const { placeService, networkService, errorService } = useRootStore()
  const classes = useStyles()

  const handleSearchEntities = async (query: string): Promise<AutocompleteResult[]> => {
    const results = await placeService.search(types, query, filter)
    const filteredResults = filterResults(results, query, MAX_RESULTS_FOR_TYPE)
    return formatResults(filteredResults, getTpModeByPlaceTypes(types), isTPPortsEnabled)
  }

  const setFormData = (organizationJSON: IOrganizationJSON): void => {
    setFormLocation(Location.create(selectedLocation as ILocationModelJSON))
    setFormOrganization(organizationJSON)
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    setIsEditing(false)
  }

  const handleError = (error: unknown) => {
    if (error instanceof Error) {
      errorService.showDialog(error.message)
    }
    closeDialog()
  }

  const toggleDialog = () => {
    setIsDialogOpen(!isDialogOpen)
  }

  const handleEditSelect = async (): Promise<void> => {
    if (!selectedLocation) {
      return
    }

    setIsEditing(true)

    try {
      const organization = await networkService.organizationsService.get(selectedLocation.organizationId)
      setFormData(organization)
      toggleDialog()
    } catch (error) {
      handleError(error)
    }
  }

  const handleDialogCompleted = (location: TLocation): Promise<ILocationModelJSON> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _id, ...newLocation } = location.locationJSON
    const locationToSave = isEditing ? location.locationJSON : newLocation

    closeDialog()
    setFormLocation(Location.create({}))
    setSelectedLocation(newLocation as ILocationModelJSON)

    return networkService.locationsService.save(locationToSave)
  }

  const processPlace = async (placeData: TPlaceData): Promise<Address | Port | ILocationModelJSON | null> => {
    if (!placeData) {
      setSelectedLocation(null)
      return Promise.resolve(null)
    }

    setSelectedLocation(isLocation(placeData) ? placeData : null)

    if (isLocation(placeData) || isPort(placeData)) {
      return Promise.resolve(placeData)
    }
    if (isPort(placeData)) {
      return Promise.resolve(placeData)
    }

    const details = await placeService.details(placeData.place_id)

    return googlePlaceToAddress(placeData, details)
  }

  const handleSelect = async (placeData: TPlaceData): Promise<void | Address | Port | ILocationModelJSON | null> => {
    if (!placeData) {
      setSelectedLocation(null)
      return Promise.resolve(null)
    }

    try {
      const place = await processPlace(placeData)
      onSelect(place as GenericLocationType)
      return place
    } catch (error) {
      handleError(error)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
  const handleFormRender = (handleEntityCreated: (newEntity: ILocationModelJSON) => any): ReactElement => {
    // "as unknown as ReactElement" to keep betterer happy without the need of refactor
    // This will be fixed with the non mobx version
    if (!canModifyDirectory) return null as unknown as ReactElement
    const handleLocationClickSave = async (location: TLocation) => {
      const entityCreated = await handleDialogCompleted(location)

      handleEntityCreated(entityCreated)
    }

    return (
      <LocationDialog
        isEditing={isEditing}
        organization={isEditing ? formOrganization : undefined}
        open={isDialogOpen}
        location={formLocation}
        searchLocation={placeService.search}
        detailsLocation={placeService.details}
        onClickCancel={closeDialog}
        getContacts={networkService.contactsService.findByIds}
        onClickSave={handleLocationClickSave}
        renderContactsAutocomplete={(props) => (
          <ContactsAutocomplete organizationId="" searchContacts={networkService.contactsService.search} {...props} />
        )}
        renderOrganizationsAutocomplete={(props) => (
          <OrganizationAutocomplete {...props} searchOrganizations={networkService.organizationsService.search} />
        )}
      />
    )
  }

  const autocompleteDefaultValue = value && placeToAutocompleteResult(value)

  return useObserver(() => (
    <div className={classes.outerContainer}>
      {canModifyDirectory && selectedLocation && (
        <IconButton className={classes.inputAction} color="default" onClick={handleEditSelect}>
          <EditIcon />
        </IconButton>
      )}
      <NetworkEntitySelector
        type="location"
        canModifyDirectory={canModifyDirectory}
        inputId={inputId}
        value={autocompleteDefaultValue as AutocompleteResult}
        placeholder={placeholder}
        onSelect={(place) => handleSelect(place as TPlaceData)}
        onFooterSelect={toggleDialog}
        onSearchEntities={handleSearchEntities}
        onSearchTextChange={onSearchTextChange}
        renderForm={handleFormRender}
        dataAttributes={dataAttributes}
      />
    </div>
  ))
}
